// -------------------------------------------------------------
// A small bridge for talking to a parent audio player
// -------------------------------------------------------------

import findIndex from 'lodash.findindex'
import uuidv4 from 'uuid/v4'
import pickBy from 'lodash.pickby'

let currentTrack = {}
let audioState = 'stopped'
let listeners = {}
let playlist = { tracks: [] }

const playerID = uuidv4()

class IFrameBridge {

    constructor () {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'new-bridge'
        }, '*')

        window.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'fbi-audio-player-event-parent' && event.data.playerID !== playerID) {
                switch (event.data.message) {
                    case 'set-playlist':
                        playlist = event.data.playlist
                        this.emitPlaylistUpdate()
                        this.sendEventToListeners('handleSetCurrentTrack', currentTrack)
                        break
                    case 'dispatch-event':
                        currentTrack.id = event.data.data.id
                        currentTrack.url = event.data.data.url
                        currentTrack.title = event.data.data.title
                        currentTrack.artist = event.data.data.artist
                        currentTrack.type = event.data.data.type
                        currentTrack.timeCode = event.data.data.timeCode
                        currentTrack.clockTime = event.data.data.clockTime
                        currentTrack.audio = event.data.data.audio
                        this.sendEventToListeners(event.data.event, event.data.data)
                        break
                }
            }
        })

        window.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'fbi-audio-player-event-child' && event.data.playerID !== playerID) {
                switch (event.data.message) {
                    case 'set-playlist':
                        this.setPlaylist(event.data.playlist)
                        break
                    case 'set-current-track':
                        this.setCurrentTrack(event.data.data.id, event.data.data.url, event.data.data.title, event.data.data.artist, event.data.data.type, event.data.data.timeCode)
                        break
                    case 'set-timecode':
                        this.setTimeCode(event.data.timeCode)
                        break
                    case 'play-stream':
                        this.playStream(event.data.data.title, event.data.data.url, event.data.data.onAirNowUrl)
                        break
                    case 'play-track':
                        this.playTrack()
                        break
                    case 'pause-track':
                        this.pauseTrack()
                        break
                    case 'stop-track':
                        this.stopTrack()
                        break
                    case 'next-track':
                        this.nextTrack()
                        break
                    case 'previous-track':
                        this.previousTrack()
                        break
                    case 'seek-seconds':
                        this.seekSeconds(event.data.seconds)
                        break;
                    case 'seek-percentage':
                        this.seekPercentage(event.data.percentage)
                        break;
                    case 'change-track-timecode':
                        this.changeTrackTimeCode(event.data.newTimeCode)
                        break;
                    case 'new-bridge': // A new bridge player was registered in a child iframe, update it with details
                        parent.postMessage({
                            type: 'fbi-audio-player-event-child',
                            playerID: playerID,
                            message: 'new-bridge',
                        }, '*')
                        break;
                }
            }
        })
    }

    setPlaylist (playlistObject) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'set-playlist',
            playlist: playlistObject
        }, '*')
    }

    getPlaylist () {
        return playlist
    }

    getCurrentTrack () {
        return currentTrack
    }

    setCurrentTrack (id, url, title, artist, type, timeCode) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'set-current-track',
            data: { id, url, title, artist, type, timeCode }
        }, '*')
    }

    setTimeCode (seconds) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'set-timecode',
            timeCode: seconds
        }, '*')
    }

    playStream (title, url, onAirNowUrl) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'play-stream',
            data: { title, url, onAirNowUrl }
        }, '*')
    }

    playTrack () {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'play-track'
        }, '*')
    }

    pauseTrack () {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'pause-track'
        }, '*')
    }

    stopTrack () {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'stop-track'
        }, '*')
    }

    // Returns true if there is a "next" track queued after this one
    nextTrackAvailable() {
        if (playlist && currentTrack) {
            const index = findIndex(playlist.tracks, track => track.id === currentTrack.id)
            if (index < playlist.tracks.length - 1) {
                return true
            }
        } else {
            return false
        }
    }

    // Returns true if there is a "previous" track queued before this one
    previousTrackAvailable() {
        if (playlist && currentTrack) {
            const index = findIndex(playlist.tracks, track => track.id === currentTrack.id)
            if (index > 0) {
                return true
            }
        } else {
            return false
        }
    }

    nextTrack () {
        // Find the current track in the playlist
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'next-track'
        }, '*')
    }

    previousTrack () {
        // Find the current track in the playlist
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'previous-track'
        }, '*')
    }

    // Seek to seconds through a track
    seekSeconds (seconds) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'seek-seconds',
            seconds: seconds
        }, '*')
    }

    // Seek a float percentage into a track (converted to seconds internally)
    seekPercentage (percentage) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'seek-percentage',
            percentage: percentage
        }, '*')
    }

    // Alters the timecode of the current track
    changeTrackTime (newTimeCode) {
        parent.postMessage({
            type: 'fbi-audio-player-event-child',
            playerID: playerID,
            message: 'change-track-timecode',
            newTimeCode: newTimeCode
        }, '*')
    }

    addListener (key, listener) {
        console.log('adding listener', key)
        listeners[key] = listener
    }

    removeListener (key) {
        delete listeners[key]
    }

    sendEventToListeners (event, data) {
        for (let key in listeners) {
            if (listeners[key][event]) {
                listeners[key][event](data)
            }
        }

        const amrapFrames = document.getElementsByClassName('amrappagesFrame')
        for (let i = 0; i < amrapFrames.length; i++) {
            const currentFrame = amrapFrames[i]
            currentFrame.contentWindow.postMessage({
                type: 'fbi-audio-player-event-parent',
                message: 'dispatch-event',
                playerID: playerID,
                event: event,
                data: {
                    audio: currentTrack.audio ? {
                        currentTime: currentTrack.audio.currentTime,
                        duration: currentTrack.audio.duration,
                        error: currentTrack.audio.error && currentTrack.audio.error.message
                    } : null,
                    id: currentTrack.id,
                    url: currentTrack.url,
                    title: currentTrack.title,
                    artist: currentTrack.artist,
                    type: currentTrack.type,
                    timeCode: currentTrack.timeCode,
                    clockTime: currentTrack.clockTime
                }
            }, '*')
        }
    }

    emitPlaylistUpdate () {
        const clonedPlaylist = pickBy(playlist, key => key !== 'tracks')
        clonedPlaylist.tracks = playlist.tracks.map((track) => {
            return {
                audio: track.audio ? {
                    currentTime: track.audio.currentTime,
                    duration: track.audio.duration,
                    error: track.audio.error && track.audio.error.message
                } : null,
                id: track.id,
                url: track.url,
                title: track.title,
                artist: track.artist,
                type: track.type,
                timeCode: track.timeCode,
                clockTime: track.clockTime
            }
        })
        const amrapFrames = document.getElementsByClassName('amrappagesFrame')
        for (let i = 0; i < amrapFrames.length; i++) {
            const currentFrame = amrapFrames[i]
            currentFrame.contentWindow.postMessage({
                type: 'fbi-audio-player-event-parent',
                message: 'set-playlist',
                playerID: playerID,
                playlist: clonedPlaylist
            }, '*')
        }
    }
}

export default IFrameBridge



// WEBPACK FOOTER //
// ./js/iframe-bridge-child.js